import { useEffect } from 'react';
import { AtlasToggle } from './components/AtlasToggle.js';
import { DebugBadge } from './components/DebugBadge.js';
import { SettingsPanel } from './components/SettingsPanel.js';
import { ConsentBanner } from './components/ConsentBanner.js';
import { DesktopHint } from './components/DesktopHint.js';
import { CrystallizationCelebration } from './components/CrystallizationCelebration.js';
import { EpochTransitionCurtain } from './components/EpochTransitionCurtain.js';
import { NameSoftAsk } from './components/NameSoftAsk.js';
import { PassiveTickers } from './components/PassiveTickers.js';
import { ReturnGreeting } from './components/ReturnGreeting.js';
import { UpgradeShowcaseOverlay } from './components/UpgradeShowcaseOverlay.js';
import { UpgradesPanel } from './components/UpgradesPanel.js';
import { UpgradesVisualSync } from './components/UpgradesVisualSync.js';
import { AtlasScreen } from './screens/AtlasScreen.js';
import { useNavigationStore } from './screens/navigation.js';
import { SparkScreen } from './screens/SparkScreen.js';
import { AudioAtmosphere } from './sounds/AudioAtmosphere.js';
import { emit } from './telemetry/emit.js';
import { FlowTrackers } from './telemetry/FlowTrackers.js';
import { isCapacitorRuntime } from './platform/android.js';
import { isTelegramRuntime } from './platform/telegram.js';
import { NetworkArtifacts } from './visual-artifacts/NetworkArtifacts.js';
import { MilestoneRevealOverlay } from './visual-artifacts/MilestoneRevealOverlay.js';
import { OnboardingDriver } from './onboarding/OnboardingDriver.js';
import { OnboardingTipOverlay } from './onboarding/OnboardingTipOverlay.js';

function detectPlatformLabel(): 'web' | 'telegram' | 'android' {
  if (isCapacitorRuntime()) return 'android';
  if (isTelegramRuntime()) return 'telegram';
  return 'web';
}

export function App(): JSX.Element {
  const screen = useNavigationStore((s) => s.current);

  // Emit a single session.start marker on first render. The companion
  // session.end fires from a `beforeunload` listener in main.tsx.
  useEffect(() => {
    emit('session.start', {
      platform: detectPlatformLabel(),
      locale: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    });
  }, []);

  return (
    <>
      {/* These components are always mounted — they don't render visible
          UI by themselves (audio scheduler, CSS-variable sync, etc.). */}
      <UpgradesVisualSync />
      <AudioAtmosphere />
      <OnboardingDriver />
      <PassiveTickers />
      {/* UX flow analytics — screen entered/exited, visibility, idle.
          Funnel firsts wires happen внутри game store actions. */}
      <FlowTrackers />

      {/* The active screen — Spark by default, Atlas on toggle. Other UI
          (Atlas toggle, Settings, Debug) stays on top in both. */}
      {screen === 'spark' && (
        <>
          <SparkScreen />
          <NetworkArtifacts />
          <UpgradesPanel />
          <UpgradeShowcaseOverlay />
          <MilestoneRevealOverlay />
          <OnboardingTipOverlay />
        </>
      )}
      {screen === 'atlas' && <AtlasScreen />}

      {/* Global overlays — visible on every screen. */}
      <AtlasToggle />
      <SettingsPanel />
      <DebugBadge />

      {/* Welcome-после-возврата toast. Mount один раз на App-уровне —
          сам проверит >12h на mount и покажет если надо. */}
      <ReturnGreeting />

      {/* Crystallization celebration — fade-in card на каждую кристал. */}
      <CrystallizationCelebration />

      {/* Epoch transition — церемония при переходе на новую эпоху.
          Срабатывает когда state.epoch увеличивается. Не блокирует игру. */}
      <EpochTransitionCurtain />

      {/* Soft-ask имя — открывает name_signature артефакт + создаёт
          первое личное вовлечение «Сай знает моё имя». Показывается
          один раз когда totalTaps>=8 и displayName ещё не установлено. */}
      <NameSoftAsk />

      {/* GDPR analytics consent — один раз при первом заходе. Не
          блокирует игру, тонкая полоса внизу. */}
      <ConsentBanner />

      {/* Desktop hint — мягкий совет открыть с телефона на широких
          экранах + точном pointer'е. Один раз, dismissable. */}
      <DesktopHint />
    </>
  );
}
